import { ShareAltOutlined } from '@ant-design/icons';
import cn from 'classnames';
import DOMPurify from 'dompurify';

import { LeadMessageText } from '@app/components/LeadCard/ui/MessageHistory/components/LeadMessageText';
import {
  LEAD_MSG_STATUS_DELETED,
  formatLeadMessageText,
  formatLeadMessageTimestamp,
  getLeadMessageStatusColor,
  getLeadMessageStatusIcon,
} from '@app/components/LeadCard/ui/MessageHistory/lib';

import type { FC } from 'react';

import './LeadTextMessage.scss';

interface LeadTextMessageProps {
  message: string;
  socialType: string | null;
  isIncoming: boolean;
  timestamp: string;
  wasEdited: boolean;
  status: string | null;
  isForwarded: boolean;
  replyMessage: string | null;
  replyMessageId: number | null;
}

export const LeadTextMessage: FC<LeadTextMessageProps> = ({
  message,
  socialType,
  isIncoming,
  timestamp,
  wasEdited,
  status,
  isForwarded,
  replyMessage,
  replyMessageId,
}) => {
  // Тело сообщения рендерится React-узлами (LeadMessageText): нужен
  // кликабельный номер телефона, а внутри innerHTML его не сделать.
  // Цитата ответа остаётся на прежнем пути с DOMPurify.
  if (!message) return null;

  const isDeleted = status === LEAD_MSG_STATUS_DELETED;
  const showStatusIcon = !isIncoming || isDeleted;
  const statusIcon = showStatusIcon ? getLeadMessageStatusIcon(status) : null;
  const statusColor = getLeadMessageStatusColor(status);
  const formattedTimestamp = formatLeadMessageTimestamp(timestamp);

  const sanitizedReplyHtml = replyMessage
    ? DOMPurify.sanitize(formatLeadMessageText(replyMessage, socialType))
    : null;

  return (
    <div
      className={cn('LeadTextMessage', {
        'LeadTextMessage--out': !isIncoming,
        'LeadTextMessage--deleted': isDeleted,
      })}
    >
      {isForwarded && (
        <div className="LeadTextMessage__forwarded">
          <ShareAltOutlined className="LeadTextMessage__forwarded-icon" />
          Пересланное сообщение
        </div>
      )}
      {sanitizedReplyHtml && (
        <div
          className="LeadTextMessage__reply"
          data-reply-target-id={replyMessageId ?? undefined}
        >
          <div className="LeadTextMessage__reply-indicator" />
          <div
            className="LeadTextMessage__reply-text"
            dangerouslySetInnerHTML={{ __html: sanitizedReplyHtml }}
          />
        </div>
      )}
      <div className="LeadTextMessage__text">
        <LeadMessageText message={message} socialType={socialType} />
      </div>
      <div className="LeadTextMessage__status">
        <span className="LeadTextMessage__timestamp">
          {wasEdited && 'изменено '}
          {formattedTimestamp}
        </span>
        {statusIcon && (
          <span
            className="LeadTextMessage__status-icon"
            style={{ color: statusColor }}
          >
            {statusIcon}
          </span>
        )}
      </div>
    </div>
  );
};
